import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import AppContext from 'src/AppContext';
import { GetLicenseSystemDetailsInput, GetLicenseSystemDetailsPayload } from './generated/types';
import { COLORCARD, CPGO } from './mappers/module';

interface LicenseSystemDetails {
    appServiceVersion: string | undefined;
    updateNumber: string | undefined;
    installedUpdateNumber: string | undefined;
    lastSeenDate: string | undefined;
    addedBy: string;
    colorProofGoUsers: string[];
    colorCardUsers: string[];
}

const mapToLicenseSystemDetails = (source: GetLicenseSystemDetailsPayload): LicenseSystemDetails => {
    const userWithAccess = source.usersWithAccess || [];

    return {
        appServiceVersion: source.computer.appServiceVersion ?? undefined,
        updateNumber: source.updateNumber?.toString() ?? undefined,
        installedUpdateNumber: source.computer.installedUpdateNumber?.toString() ?? undefined,
        lastSeenDate: source.computer.lastSeenDate ?? undefined,
        addedBy: source.addedByAdmin.email,
        colorProofGoUsers: userWithAccess
            .filter((user) => user.moduleId === CPGO.id)
            .map((user) => user.email),
        colorCardUsers: userWithAccess
            .filter((user) => user.moduleId === COLORCARD.id)
            .map((user) => user.email),
    };
};

const query = `query GetLicenseSystemDetails($input: GetLicenseSystemDetailsInput!) {
    getLicenseSystemDetails(input: $input) {
      addedByAdmin {
        email
      }
      computer {
        appServiceVersion
                installedUpdateNumber
        lastSeenDate
      }
      updateNumber
      updateTimeStamp
      usersWithAccess {
        email
        moduleId
      }
    }
  }`;

export const useGetLicenseSystemDetails = (licenseSystemId: string, orgId: string) => {
    const context = useContext(AppContext);

    const input: GetLicenseSystemDetailsInput = {
        licenseSystemId,
        orgId,
    };

    return useQuery<LicenseSystemDetails | undefined, Error>({
        queryKey: ['getLicenseSystemDetails', licenseSystemId],
        queryFn: async (): Promise<LicenseSystemDetails> => {

            const response = await (context.api.graphql({ query, variables: { input } }) as Promise<{
                data: {
                    getLicenseSystemDetails: GetLicenseSystemDetailsPayload;
                };
            }>);

            return mapToLicenseSystemDetails(response?.data?.getLicenseSystemDetails);

        },
        enabled: !!licenseSystemId,
        refetchOnWindowFocus: false,
    });
};
